import React, { useState, useEffect } from "react";
import { Modal, FormInput, FormTextarea, Button, SearchableSelect } from "@/components/ui";
import { Calendar } from "lucide-react";
import { appointmentService } from "../services/appointmentService";

/* Developer: Nithin | Date: 09-07-2026 | Changes: Added time field, no-backdate validation, smart button text (Schedule / Update), pre-fill from existing appointmentDate */
export const UpdateAppointmentForm = ({ isOpen, onClose, onSubmit, initialData, title = "Reschedule Appointment", subtitle = "Select a date for the site visit or meeting." }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [dateError, setDateError] = useState("");
  const [execError, setExecError] = useState("");
  const [teams, setTeams] = useState([]);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await appointmentService.getTeams();
        const allTeams = response.data?.data || [];
        const filteredTeams = allTeams.filter(u => ['designer', 'admin'].includes(u.role));
        setTeams(filteredTeams);
      } catch (error) {
        console.error("Failed to fetch teams:", error);
      }
    };
    if (isOpen) {
      fetchTeams();
    }
  }, [isOpen]);

  useEffect(() => {
    if (initialData && isOpen) {
      let date = today;
      let time = "";
      if (initialData.appointmentDate) {
        const d = new Date(initialData.appointmentDate);
        date = d.toISOString().split('T')[0];
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        time = `${hh}:${mm}`;
      }
      const execVal = typeof initialData.siteExecutive === 'object' && initialData.siteExecutive !== null
        ? initialData.siteExecutive._id
        : (initialData.siteExecutive || "");
      setFormData({ appointmentDate: date, appointmentTime: time, notes: "", siteExecutive: execVal });
      setDateError("");
      setExecError("");
    }
  }, [initialData, isOpen]);

  if (!isOpen || !initialData) return null;

  const hasExistingDate = !!initialData.appointmentDate;
  const fullName = initialData?.customerName || `${initialData?.firstName || ""} ${initialData?.lastName || ""}`.trim() || "No Name";

  const handleDateChange = (e) => {
    const val = e.target.value;
    if (val < today) {
      setDateError("⚠️ You are trying to select a past date — invalid! Please select today or a future date.");
    } else {
      setDateError("");
    }
    setFormData({ ...formData, appointmentDate: val });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasErr = false;
    if (formData.appointmentDate < today) {
      setDateError("⚠️ You are trying to select a past date — invalid! Please select today or a future date.");
      hasErr = true;
    }
    if (!formData.siteExecutive) {
      setExecError("Please assign a designer");
      hasErr = true;
    }
    if (hasErr) return;
    setLoading(true);
    try {
      // Combine date + time into ISO string
      const timeStr = formData.appointmentTime || "00:00";
      const appointmentISO = `${formData.appointmentDate}T${timeStr}:00`;
      const dataToSubmit = {
        appointmentDate: appointmentISO,
        siteExecutive: formData.siteExecutive,
      };
      if (formData.notes) {
        dataToSubmit.appointmentNote = formData.notes;
        dataToSubmit.activityNote = formData.notes;
      }
      await onSubmit(initialData._id, dataToSubmit);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
    >
      <div className="space-y-5">
        <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
          <div className="w-10 h-10 rounded-full bg-brand-blue/10 flex items-center justify-center font-bold text-brand-blue uppercase">
            {fullName.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-brand-dark truncate">{fullName}</div>
            <div className="text-[10px] text-slate-400 font-medium">
              {hasExistingDate ? "Updating Appointment Details" : "Scheduling New Appointment"}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 rounded-xl border border-orange-100 flex items-start gap-3">
              <div className="p-2 bg-orange-100 text-orange-600 rounded-lg">
                <Calendar size={20} />
              </div>
              <div>
                <div className="text-sm font-bold text-orange-800">{title}</div>
                <div className="text-xs text-orange-600 mt-0.5">{subtitle}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <FormInput
                  label="Appointment Date *"
                  type="date"
                  value={formData.appointmentDate || ""}
                  onChange={handleDateChange}
                  min={today}
                  required
                />
                {dateError && (
                  <p className="text-xs text-red-500 font-medium mt-0.5">{dateError}</p>
                )}
              </div>
              <FormInput
                label="Time (Opt.)"
                type="time"
                value={formData.appointmentTime || ""}
                onChange={e => setFormData({ ...formData, appointmentTime: e.target.value })}
              />
            </div>

            <div className="flex flex-col gap-1">
              <SearchableSelect
                label="Assign to Designer *"
                options={teams.map(u => {
                  const name = u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : (u.name || u.username);
                  const role = u.role ? ` (${u.role.replace(/[-_]/g, ' ')})` : '';
                  return { value: u._id, label: `${name}${role}` };
                })}
                value={formData.siteExecutive || ""}
                onChange={val => {
                  setFormData({ ...formData, siteExecutive: val });
                  if (val) setExecError("");
                }}
                placeholder="Select Designer"
                error={execError}
              />
            </div>

            <FormTextarea
              label="Transition Notes / Activity Comment"
              placeholder="What happened? Why the change?"
              value={formData.notes || ""}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              className="flex-1"
              loading={loading}
              variant="orange"
              disabled={!!dateError}
            >
              {hasExistingDate ? "Update Appointment" : "Schedule Appointment"}
            </Button>
            <Button variant="secondary" onClick={onClose} className="px-6">Cancel</Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
